import { useCallback, useEffect, useRef, useState } from 'react';
import { useCreateRealtimeSessionMutation } from '@features/transcription';

interface UseRealtimeTranscriptionOptions {
  /**
   * If true, fetches a session token immediately on mount.
   * If false (default), waits until refreshToken() is called manually.
   * Set to false for lazy loading when recording might not be used.
   */
  fetchOnMount?: boolean;
}

interface UseRealtimeTranscriptionReturn {
  token: string | null;
  isLoading: boolean;
  error: string | null;
  refreshToken: () => Promise<void>;
  expiresAt: Date | null;
}

/**
 * Hook to manage AssemblyAI realtime transcription session tokens
 *
 * Features:
 * - Optional lazy loading (fetchOnMount: false) to avoid unnecessary session creation
 * - Tracks token expiration
 * - Provides refresh functionality
 * - Automatic cleanup
 */
export const useRealtimeTranscription = (
  options: UseRealtimeTranscriptionOptions = {},
): UseRealtimeTranscriptionReturn => {
  const { fetchOnMount = false } = options;

  const [token, setToken] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [createSession, { isLoading }] = useCreateRealtimeSessionMutation();
  const isUnmountedRef = useRef(false);

  const refreshToken = useCallback(async () => {
    if (isUnmountedRef.current) return;

    try {
      setError(null);

      // Request token with 5 minute expiration (300 seconds)
      const result = await createSession({
        createRealtimeSessionRequest: {
          expires_in_seconds: 300,
        },
      }).unwrap();

      if (isUnmountedRef.current) return;

      setToken(result.token);

      // Calculate expiration time
      const expirationDate = new Date(Date.now() + result.expires_in_seconds * 1000);
      setExpiresAt(expirationDate);
    } catch (err: any) {
      if (isUnmountedRef.current) return;

      const errorMessage =
        err?.data?.message ||
        err?.message ||
        'Failed to create transcription session. Please try again.';
      setError(errorMessage);
      setToken(null);
      setExpiresAt(null);
    }
  }, [createSession]);

  // Only fetch token on mount if explicitly requested
  useEffect(() => {
    if (fetchOnMount) {
      refreshToken();
    }
  }, [fetchOnMount, refreshToken]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  return {
    token,
    isLoading,
    error,
    refreshToken,
    expiresAt,
  };
};
